import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import type {
  TenantCustomer,
  TenantCustomerListItem,
  TenantCustomerUserDetail
} from "../lib/customers.js";
import { CustomerUserMappingTable } from "../components/CustomerUserMappingTable.js";

interface DetailResponse {
  customer: TenantCustomer;
  users: TenantCustomerUserDetail[];
  children: TenantCustomer[];
}

/**
 * Tenant admin — Customer detail (ADR-0075 §3.2).
 *
 * Route: `/app/admin/customers/:id`. Three cards: customer fields,
 * mapped users, children. Customer fields autosave on blur.
 */
export function CustomerDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();

  const [data, setData] = useState<DetailResponse | null>(null);
  const [allCustomers, setAllCustomers] = useState<TenantCustomerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<Record<string, string>>({});

  // Local form state (autosaves on blur).
  const [displayName, setDisplayName] = useState("");
  const [externalId, setExternalId] = useState("");
  const [notes, setNotes] = useState("");
  const [parentId, setParentId] = useState<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, list] = await Promise.all([
        apiFetch<DetailResponse>(`/api/tenant/admin/customers/${id}`),
        apiFetch<{ customers: TenantCustomerListItem[] }>("/api/tenant/admin/customers")
      ]);
      setData(detail);
      setAllCustomers(list.customers);
      setDisplayName(detail.customer.display_name);
      setExternalId(detail.customer.external_id);
      setNotes(detail.customer.notes ?? "");
      setParentId(detail.customer.parent_customer_id ?? "");
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(body: Record<string, unknown>, fieldKey?: string) {
    try {
      const res = await apiFetch<{ customer: TenantCustomer }>(
        `/api/tenant/admin/customers/${id}`,
        { method: "PATCH", body }
      );
      setData((d) => (d ? { ...d, customer: res.customer } : d));
      if (fieldKey) setFieldError((p) => ({ ...p, [fieldKey]: "" }));
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      if (fieldKey) setFieldError((p) => ({ ...p, [fieldKey]: msg }));
      else toast.push({ kind: "error", message: msg });
    }
  }

  async function deleteCustomer() {
    if (!data) return;
    try {
      await apiFetch(`/api/tenant/admin/customers/${id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Customer deleted." });
      nav("/app/admin/customers");
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  if (loading && !data) {
    return (
      <PageLayout title="Customer">
        <LoadingState />
      </PageLayout>
    );
  }
  if (error && !data) {
    return (
      <PageLayout title="Customer">
        <ErrorState detail={error} />
      </PageLayout>
    );
  }
  if (!data) return null;

  const { customer, users, children } = data;
  const sortedChildren = [...children].sort((a, b) =>
    a.display_name.localeCompare(b.display_name, undefined, { sensitivity: "base" })
  );

  // Filter out self + descendants from parent options.
  const descendantIds = new Set<string>();
  function markDescendants(targetId: string): void {
    for (const c of allCustomers) {
      if (c.parent_customer_id === targetId && !descendantIds.has(c.id)) {
        descendantIds.add(c.id);
        markDescendants(c.id);
      }
    }
  }
  markDescendants(customer.id);
  const parentOptions = allCustomers.filter(
    (c) => c.id !== customer.id && !descendantIds.has(c.id)
  );

  return (
    <PageLayout
      title={
        <>
          <Link to="/app/admin/customers">Account Coverage</Link>
          {" / "}
          <span className="mono">{customer.external_id}</span>
          {": "}
          {customer.display_name}
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {/* Customer card */}
        <div className="card">
          <div style={{ display: "grid", gap: 10, maxWidth: 720 }}>
            <label className="field">
              <span className="label">Display name</span>
              <input
                className="input"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onBlur={() =>
                  displayName.trim() !== customer.display_name &&
                  void patch({ display_name: displayName.trim() }, "display_name")
                }
              />
              {fieldError.display_name && (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  {fieldError.display_name}
                </div>
              )}
            </label>
            <label className="field">
              <span className="label">Account Code</span>
              <input
                className="input"
                type="text"
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                onBlur={() =>
                  externalId.trim() !== customer.external_id &&
                  void patch({ external_id: externalId.trim() }, "external_id")
                }
              />
              {fieldError.external_id && (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  {fieldError.external_id}
                </div>
              )}
            </label>
            <label className="field">
              <span className="label">Parent customer</span>
              <select
                className="select"
                value={parentId}
                onChange={(e) => {
                  setParentId(e.target.value);
                  void patch(
                    { parent_customer_id: e.target.value === "" ? null : e.target.value },
                    "parent_customer_id"
                  );
                }}
              >
                <option value="">(none — top-level)</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
              {fieldError.parent_customer_id && (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  {fieldError.parent_customer_id}
                </div>
              )}
            </label>
            <label className="field">
              <span className="label">Notes</span>
              <textarea
                className="input"
                value={notes}
                rows={3}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() =>
                  notes !== (customer.notes ?? "") &&
                  void patch({ notes: notes === "" ? null : notes }, "notes")
                }
              />
            </label>
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn btnDanger" onClick={() => void deleteCustomer()}>
                Delete customer
              </button>
            </div>
          </div>
        </div>

        {/* Mapped users card */}
        <CustomerUserMappingTable
          tenantCustomerId={customer.id}
          users={users}
          onChanged={() => void load()}
        />

        {/* Sub-customers card */}
        {children.length > 0 && (
          <div className="card">
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Sub-customers</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
              {sortedChildren.map((child) => (
                <li key={child.id}>
                  <Link to={`/app/admin/customers/${child.id}`}>
                    <span className="mono">{child.external_id}</span>: {child.display_name}
                  </Link>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 10 }}>
              <Link className="btn" to={`/app/admin/customers/new?parent_id=${customer.id}`}>
                + New sub-customer
              </Link>
            </div>
          </div>
        )}
        {children.length === 0 && (
          <div className="card">
            <div className="muted">
              No sub-teams.{" "}
              <Link to={`/app/admin/customers/new?parent_id=${customer.id}`}>+ New sub-customer</Link>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
